import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { adminOSImageService, toFormValues } from '@/services/admin-os-image.service'
import type { AdminOSImageFilters, AdminOSImage, OSImageFormValues } from '@/types/admin'

/**
 * Query keys for OS Image management
 *
 * Structured hierarchy:
 * - ['admin', 'os-images'] - All OS image queries
 * - ['admin', 'os-images', 'list'] - List queries
 * - ['admin', 'os-images', 'list', filters] - Filtered list
 * - ['admin', 'os-images', 'detail'] - Detail queries
 * - ['admin', 'os-images', 'detail', id] - Specific image detail
 */
export const osImageKeys = {
  all: ['admin', 'os-images'] as const,
  lists: () => [...osImageKeys.all, 'list'] as const,
  list: (filters: AdminOSImageFilters) => [...osImageKeys.lists(), filters] as const,
  details: () => [...osImageKeys.all, 'detail'] as const,
  detail: (id: string) => [...osImageKeys.details(), id] as const,
}

// ============================================================================
// Queries
// ============================================================================

/**
 * Get paginated list of OS images with filters
 *
 * @param filters - Filter parameters (search, category, osType, status, etc.)
 * @returns Paginated list of OS images
 */
export function useAdminOSImages(filters: AdminOSImageFilters) {
  return useQuery({
    queryKey: osImageKeys.list(filters),
    queryFn: () => adminOSImageService.getAll(filters),
    // Keep the previous page on screen while a filter or page change loads — the list screen shows
    // an "Updating…" pill over those rows instead of blanking the table.
    placeholderData: (previousData) => previousData,
    staleTime: 30_000, // 30 seconds
    gcTime: 5 * 60_000, // 5 minutes
  })
}

/**
 * Get OS image detail by ID
 *
 * @param id - OS image ID (UUID)
 * @returns OS image detail
 */
export function useAdminOSImage(id: string) {
  return useQuery({
    queryKey: osImageKeys.detail(id),
    queryFn: () => adminOSImageService.getById(id),
    staleTime: 60_000, // 60 seconds
    gcTime: 10 * 60_000, // 10 minutes
    enabled: !!id,
  })
}

/**
 * @deprecated Use useAdminOSImage instead
 */
export const useAdminOSImageDetail = useAdminOSImage

// ============================================================================
// Mutations
// ============================================================================

/**
 * Create new OS image
 *
 * Invalidates:
 * - All OS image list queries
 */
export function useCreateOSImage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (values: OSImageFormValues) => adminOSImageService.create(values),
    onSuccess: (osImage) => {
      // Invalidate all list queries
      queryClient.invalidateQueries({ queryKey: osImageKeys.lists() })
      toast.success('OS image created successfully', {
        description: `${osImage.name} has been created`,
      })
    },
    onError: (error: Error) => {
      toast.error('Failed to create OS image', {
        description: error.message,
      })
    },
  })
}

/**
 * Update OS image
 *
 * Invalidates:
 * - Specific image detail query
 * - All list queries
 */
export function useUpdateOSImage() {
  const queryClient = useQueryClient()

  return useMutation({
    // `current` carries the row being edited so the service can preserve the JSON keys the
    // form does not render (cost, tags, cloudInit/rdpEnabled, ...).
    mutationFn: ({
      id,
      values,
      current,
    }: {
      id: string
      values: OSImageFormValues
      current?: AdminOSImage
    }) => adminOSImageService.update(id, values, current),
    onSuccess: (response, { id }) => {
      // Invalidate specific detail query
      queryClient.invalidateQueries({ queryKey: osImageKeys.detail(id) })
      // Invalidate all list queries
      queryClient.invalidateQueries({ queryKey: osImageKeys.lists() })
      toast.success('OS image updated successfully', {
        description: response.message,
      })
    },
    onError: (error: Error) => {
      toast.error('Failed to update OS image', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete OS image
 *
 * Invalidates:
 * - All list queries
 * - Specific detail query
 */
export function useDeleteOSImage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adminOSImageService.delete(id),
    onSuccess: (response, id) => {
      // Invalidate all list queries
      queryClient.invalidateQueries({ queryKey: osImageKeys.lists() })
      // Remove specific detail query
      queryClient.removeQueries({ queryKey: osImageKeys.detail(id) })
      toast.success('OS image deleted successfully', {
        description: response.message,
      })
    },
    onError: (error: any) => {
      // Handle 409 Conflict (image in use)
      if (error.statusCode === 409 || error.code === 'CONFLICT') {
        toast.error('Cannot delete OS image', {
          description:
            'This image is currently in use by one or more VMs. Please remove all VMs using this image before deleting.',
          duration: 5000,
        })
      } else {
        toast.error('Failed to delete OS image', {
          description: error.message,
        })
      }
    },
  })
}

/**
 * Toggle OS image active status
 *
 * Convenience mutation for quickly activating/deactivating an image.
 * Uses optimistic updates for instant UI feedback.
 * Invalidates same queries as update.
 */
export function useToggleOSImageActive() {
  const queryClient = useQueryClient()

  return useMutation({
    // The backend PUT replaces the whole row, so a status toggle has to resend every field —
    // it rebuilds the payload from the row it is toggling rather than sending isActive alone.
    mutationFn: ({ image, isActive }: { image: AdminOSImage; isActive: boolean }) =>
      adminOSImageService.update(image.id, { ...toFormValues(image), isActive }, image),
    onMutate: async ({ image, isActive }) => {
      const id = image.id
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: osImageKeys.detail(id) })

      // Snapshot previous value
      const previousImage = queryClient.getQueryData<AdminOSImage>(osImageKeys.detail(id))

      // Optimistically update
      if (previousImage) {
        queryClient.setQueryData<AdminOSImage>(osImageKeys.detail(id), {
          ...previousImage,
          isActive,
        })
      }

      return { previousImage }
    },
    onSuccess: (_, { image, isActive }) => {
      // Invalidate queries to ensure sync with server
      queryClient.invalidateQueries({ queryKey: osImageKeys.lists() })
      queryClient.invalidateQueries({ queryKey: osImageKeys.detail(image.id) })
      toast.success(`OS image ${isActive ? 'activated' : 'deactivated'}`)
    },
    onError: (error: Error, { image }, context) => {
      // Rollback on error
      if (context?.previousImage) {
        queryClient.setQueryData(osImageKeys.detail(image.id), context.previousImage)
      }
      toast.error('Failed to update OS image status', {
        description: error.message,
      })
    },
  })
}

/**
 * Bulk activate OS images
 *
 * Invalidates:
 * - All list queries
 */
export function useBulkActivateOSImages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => adminOSImageService.bulkActivate(ids),
    onSuccess: (response, ids) => {
      // Invalidate all list queries
      queryClient.invalidateQueries({ queryKey: osImageKeys.lists() })
      // Invalidate all affected detail queries
      ids.forEach((id) => {
        queryClient.invalidateQueries({ queryKey: osImageKeys.detail(id) })
      })
      toast.success(`${response.affectedCount} OS image(s) activated`, {
        description: response.message,
      })
    },
    onError: (error: Error) => {
      toast.error('Failed to activate OS images', {
        description: error.message,
      })
    },
  })
}

/**
 * Bulk deactivate OS images
 *
 * Invalidates:
 * - All list queries
 */
export function useBulkDeactivateOSImages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => adminOSImageService.bulkDeactivate(ids),
    onSuccess: (response, ids) => {
      // Invalidate all list queries
      queryClient.invalidateQueries({ queryKey: osImageKeys.lists() })
      // Invalidate all affected detail queries
      ids.forEach((id) => {
        queryClient.invalidateQueries({ queryKey: osImageKeys.detail(id) })
      })
      toast.success(`${response.affectedCount} OS image(s) deactivated`, {
        description: response.message,
      })
    },
    onError: (error: Error) => {
      toast.error('Failed to deactivate OS images', {
        description: error.message,
      })
    },
  })
}

/**
 * Bulk delete OS images
 *
 * Invalidates:
 * - All list queries
 */
export function useBulkDeleteOSImages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => adminOSImageService.bulkDelete(ids),
    onSuccess: (response, ids) => {
      // Invalidate all list queries
      queryClient.invalidateQueries({ queryKey: osImageKeys.lists() })
      // Remove all affected detail queries
      ids.forEach((id) => {
        queryClient.removeQueries({ queryKey: osImageKeys.detail(id) })
      })
      toast.success(`${response.affectedCount} OS image(s) deleted`, {
        description: response.message,
      })
    },
    onError: (error: Error) => {
      toast.error('Failed to delete OS images', {
        description: error.message,
      })
    },
  })
}
